import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export type CanvasWorkflowId =
  | "new-lead-qualification"
  | "idle-lead-winback"
  | "instagram-comment-dm"
  | "post-session-feedback"
  | "trial-to-paid"
  | "dormant-winback"
  | "complaint-recovery"
  | "birthday-milestone-message";

// The orchestrator stores both the canvas ids and the older snake_case ids.
export const DECISION_WORKFLOW_IDS: Record<CanvasWorkflowId, string[]> = {
  "new-lead-qualification": ["new_lead_qualification", "new-lead-qualification"],
  "idle-lead-winback": ["dormant_lead_winback", "idle-lead-winback"],
  "instagram-comment-dm": ["instagram_comment_dm", "instagram-comment-dm"],
  "post-session-feedback": ["post_session_feedback", "post-session-feedback"],
  "trial-to-paid": ["trial_to_paid", "trial-to-paid"],
  "dormant-winback": ["dormant_winback", "dormant-winback"],
  "complaint-recovery": ["complaint_recovery", "complaint-recovery"],
  "birthday-milestone-message": ["birthday_milestone_message", "birthday-milestone-message"],
};

const WORKFLOW_NAMES: Record<CanvasWorkflowId, string> = {
  "new-lead-qualification": "Convert a new lead",
  "idle-lead-winback": "Re-engage a new lead that ghosts",
  "instagram-comment-dm": "Reply to comments with a DM",
  "post-session-feedback": "Ask for feedback after the first session",
  "trial-to-paid": "Turn trial members into paying customers",
  "dormant-winback": "Bring back customers who stopped booking",
  "complaint-recovery": "Recover from customer complaints",
  "birthday-milestone-message": "Celebrate birthdays and milestones",
};

export interface OrchestratorDecision {
  id: string;
  action: string;
  reason: string;
  created_at: string;
  selected_workflow_id: string | null;
  contact_id: string | null;
}

type Outcome = "activated" | "paused" | "staff" | "skipped";

export function decisionOutcome(action: string): { outcome: Outcome; label: string } {
  if (action === "activate_workflow") return { outcome: "activated", label: "Activated" };
  if (action === "skip_active_hold") return { outcome: "paused", label: "Paused (hold)" };
  if (action === "skip_cooling_off") return { outcome: "paused", label: "Paused (cooldown)" };
  if (action === "skip_duplicate_active") return { outcome: "paused", label: "Paused (active)" };
  if (action === "escalate_to_human") return { outcome: "staff", label: "Staff takeover" };
  return { outcome: "skipped", label: "Skipped" };
}

export function formatRelativeTime(isoString: string): string {
  const diffSec = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (Number.isNaN(diffSec)) return "";
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}

// Same tinted pill treatment as the customer status badges.
const OUTCOME_BADGE: Record<Outcome, string> = {
  activated: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  paused: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  staff: "bg-sky-500/15 text-sky-700 border-sky-500/30",
  skipped: "bg-muted text-muted-foreground border-border",
};

const workflowKeyFor = (selectedWorkflowId: string | null): CanvasWorkflowId | null =>
  (Object.entries(DECISION_WORKFLOW_IDS).find(([, ids]) => selectedWorkflowId && ids.includes(selectedWorkflowId))?.[0] as CanvasWorkflowId | undefined) ?? null;

export const workflowDisplayName = (selectedWorkflowId: string | null): string | null => {
  const key = workflowKeyFor(selectedWorkflowId);
  return key ? WORKFLOW_NAMES[key] : null;
};

// Dev-only sample rows so the Runs layout can be vetted without seeding the
// database. Never shown in a production build, and only when there is no real data.
const minutesAgo = (minutes: number) => new Date(Date.now() - minutes * 60_000).toISOString();
const SAMPLE_CUSTOMERS: Record<string, string> = {
  "sample-1": "Samantha Lee", "sample-2": "Rachel Goh", "sample-3": "Nur Izzati", "sample-4": "Hannah Teo",
  "sample-5": "Marcus Lim", "sample-6": "Aisha Rahman", "sample-7": "Daniel Koh",
};
const SAMPLE_DECISIONS: OrchestratorDecision[] = [
  { id: "sample-d1", contact_id: "sample-1", selected_workflow_id: "new-lead-qualification", action: "activate_workflow", created_at: minutesAgo(2), reason: "New enquiry about trial classes. No active conversation, so this workflow started." },
  { id: "sample-d2", contact_id: "sample-2", selected_workflow_id: "idle-lead-winback", action: "skip_cooling_off", created_at: minutesAgo(180), reason: "Contacted by this workflow yesterday, so it waited out the cooldown." },
  { id: "sample-d3", contact_id: "sample-3", selected_workflow_id: "new-lead-qualification", action: "escalate_to_human", created_at: minutesAgo(60 * 26), reason: "Asked to speak to a person, so the AI handed the chat to staff." },
  { id: "sample-d4", contact_id: "sample-4", selected_workflow_id: "idle-lead-winback", action: "activate_workflow", created_at: minutesAgo(60 * 30), reason: "New enquiry went quiet for 3 days with no reply, so a single follow-up was started." },
  { id: "sample-d5", contact_id: "sample-5", selected_workflow_id: "new-lead-qualification", action: "skip_duplicate_active", created_at: minutesAgo(60 * 49), reason: "This lead already has an active conversation with this workflow." },
  { id: "sample-d6", contact_id: "sample-6", selected_workflow_id: "new-lead-qualification", action: "activate_workflow", created_at: minutesAgo(60 * 72), reason: "New enquiry via Instagram asking about pricing. Workflow started." },
  { id: "sample-d7", contact_id: "sample-7", selected_workflow_id: "idle-lead-winback", action: "skip_active_hold", created_at: minutesAgo(60 * 96), reason: "Staff placed a hold on this customer, so outreach was paused." },
  { id: "sample-d8", contact_id: "sample-1", selected_workflow_id: "idle-lead-winback", action: "skip_no_match", created_at: minutesAgo(60 * 120), reason: "Customer replied after the last message, so no follow-up was needed." },
];

interface WorkflowActivityProps {
  businessId: string | null | undefined;
  initialWorkflow?: CanvasWorkflowId | null;
}

const WorkflowActivity = ({ businessId, initialWorkflow = null }: WorkflowActivityProps) => {
  const [workflow, setWorkflow] = useState<CanvasWorkflowId | "all">(initialWorkflow ?? "all");
  const [outcome, setOutcome] = useState<Outcome | "all">("all");
  const [query, setQuery] = useState("");

  const decisions = useQuery({
    queryKey: ["workflow-activity-decisions", businessId],
    enabled: !!businessId,
    refetchInterval: 30_000,
    queryFn: async (): Promise<OrchestratorDecision[]> => {
      const { data, error } = await (supabase as any)
        .from("orchestrator_decisions")
        .select("id, action, reason, created_at, selected_workflow_id, contact_id")
        .eq("business_id", businessId)
        .in("selected_workflow_id", Object.values(DECISION_WORKFLOW_IDS).flat())
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as OrchestratorDecision[];
    },
  });

  const contactIds = [...new Set((decisions.data ?? []).map(row => row.contact_id).filter((id): id is string => !!id))];
  const contacts = useQuery({
    queryKey: ["workflow-activity-contacts", businessId, contactIds.join(",")],
    enabled: contactIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contacts")
        .select("contact_id, display_name, primary_email, primary_phone")
        .in("contact_id", contactIds);
      if (error) throw error;
      return Object.fromEntries((data ?? []).map((c: any) => [c.contact_id, c.display_name || c.primary_email || c.primary_phone || "Unknown customer"]));
    },
  });
  const showSamples = import.meta.env.DEV && !decisions.isLoading && !decisions.data?.length;
  const customerName = (id: string | null) => (id && (contacts.data?.[id] ?? SAMPLE_CUSTOMERS[id])) || "Unknown customer";

  const workflowRows = (showSamples ? SAMPLE_DECISIONS : decisions.data ?? []).filter(row => workflow === "all" || workflowKeyFor(row.selected_workflow_id) === workflow);

  const term = query.trim().toLocaleLowerCase();
  const rows = workflowRows.filter(row => {
    if (outcome !== "all" && decisionOutcome(row.action).outcome !== outcome) return false;
    return !term || [customerName(row.contact_id), row.reason].join(" ").toLocaleLowerCase().includes(term);
  });

  return <div className="min-h-0 flex-1 overflow-y-auto bg-card" data-testid="workflow-activity">
    <div className="flex flex-wrap items-center gap-2 px-5 pb-3 pt-4">
      <div className="relative w-full max-w-[300px]">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input type="search" aria-label="Search runs" placeholder="Search customer or reason..." value={query} onChange={event => setQuery(event.target.value)} className="admin-toolbar-input pl-8" />
      </div>
      <Select value={workflow} onValueChange={value => setWorkflow(value as CanvasWorkflowId | "all")}>
        <SelectTrigger aria-label="Filter by workflow" className="admin-toolbar-button w-auto gap-2 font-normal"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All workflows</SelectItem>
          {(Object.keys(WORKFLOW_NAMES) as CanvasWorkflowId[]).map(id => <SelectItem key={id} value={id}>{WORKFLOW_NAMES[id]}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={outcome} onValueChange={value => setOutcome(value as Outcome | "all")}>
        <SelectTrigger aria-label="Filter by outcome" className="admin-toolbar-button w-auto gap-2 font-normal"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All outcomes</SelectItem>
          <SelectItem value="activated">Activated</SelectItem>
          <SelectItem value="paused">Paused</SelectItem>
          <SelectItem value="staff">Staff takeover</SelectItem>
          <SelectItem value="skipped">Skipped</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div className="px-5 pb-5">
      <div className="admin-data-table-shell">
        <Table className="admin-data-table min-w-[720px]">
          <TableHeader className="admin-data-table-header">
            <TableRow className="admin-data-table-header-row">
              <TableHead className="w-28 px-3">When</TableHead>
              <TableHead className="w-48 px-3">Customer</TableHead>
              <TableHead className="w-60 px-3">Workflow</TableHead>
              <TableHead className="w-44 px-3">Outcome</TableHead>
              <TableHead className="px-3">Reason</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="admin-data-table-body">
            {decisions.isLoading ? <TableRow><TableCell colSpan={5} className="py-16 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading runs" /></TableCell></TableRow>
              : decisions.isError ? <TableRow><TableCell colSpan={5} role="alert" className="py-16 text-center text-sm text-muted-foreground">Runs couldn’t be loaded. Try again in a moment.</TableCell></TableRow>
              : rows.length === 0 ? <TableRow><TableCell colSpan={5} className="py-16 text-center"><p className="text-sm font-medium">{decisions.data?.length || showSamples ? "No matching runs" : "No runs yet"}</p><p className="mt-1 text-xs text-muted-foreground">{decisions.data?.length || showSamples ? "Try another filter." : "When the Orchestration Agent evaluates a workflow for a customer, it shows up here."}</p></TableCell></TableRow>
              : rows.map(row => {
                const { outcome: kind, label } = decisionOutcome(row.action);
                const key = workflowKeyFor(row.selected_workflow_id);
                return <TableRow key={row.id} className="admin-data-table-row">
                  <TableCell className="admin-data-table-cell whitespace-nowrap text-sm text-muted-foreground" title={new Date(row.created_at).toLocaleString("en-SG")}>{formatRelativeTime(row.created_at)}</TableCell>
                  <TableCell className="admin-data-table-cell truncate text-sm font-medium">{customerName(row.contact_id)}</TableCell>
                  <TableCell className="admin-data-table-cell truncate text-sm text-muted-foreground">{key ? WORKFLOW_NAMES[key] : "—"}</TableCell>
                  <TableCell className="admin-data-table-cell"><span className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium ${OUTCOME_BADGE[kind]}`}>{label}</span></TableCell>
                  <TableCell className="admin-data-table-cell truncate text-sm text-muted-foreground" title={row.reason}>{row.reason}</TableCell>
                </TableRow>;
              })}
          </TableBody>
        </Table>
      </div>
    </div>
  </div>;
};

export default WorkflowActivity;
